import React, { useState } from 'react';
import { isEmpty } from '@techchange/pm-specs';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
	FormFeedback,
	FormGroup,
	FormText,
	Label,
} from 'reactstrap';

import { appPropTypes, SCOPES } from '../../../_shared';
import { messages } from '../../../_shared/i18n/messages';
import { ENUMS_VIDEO, videoTypes } from '../../../_video';

import { FIELD_TYPES } from '../../constants';
import {
	CheckboxInput,
	CheckboxesInput,
	DateTimeInput,
	DateTimeRangeInput,
	FileInput,
	Input,
	LikertScaleInput,
	LocationInput,
	PasswordInput,
	PercentageInput,
	RadioInput,
	SelectInput,
	SwitchInput,
	TextEditorInput,
	VideoInput,
} from '../FormInput';
import TextEditorInline from '../TextEditorInline';

import './FormField.scss';

const FILE_UPLOAD_FIELD_PROP_TYPES = {

	/**
	 * True, if the user is the previewing the form.
	 *
	 * NOTE: Only used for file inputs.
	 */
	isPreview: PropTypes.bool,

	/**
	 * The type of files to allow for upload.
	 *
	 * NOTE: Only used for file inputs.
	 */
	accept: PropTypes.arrayOf(PropTypes.string),

	/**
	 * The visibility settings of the file(s) to upload.
	 *
	 * NOTE: Only used for file inputs.
	 */
	visibility: PropTypes.oneOf(['public', 'private']),

	/**
	 * The settings for filestack (required when editing!).
	 *
	 * NOTE: Only used for file inputs.
	 */
	filestack: PropTypes.shape({

		// The API key
		apiKey: PropTypes.string,

		// The security policy and signature
		security: PropTypes.shape({

			policy: PropTypes.string,

			signature: PropTypes.string,

		}),

		// Flag indicating if mode is production
		production: PropTypes.bool,

		// The path
		path: PropTypes.string,

		// The container
		container: PropTypes.string,

		// The region
		region: PropTypes.string,

	}),

};

/**
 * Prop types that are required for Video Input form fields only
 */
const VIDEO_INPUT_FIELD_PROP_TYPES = {

	/**
	 * The scope.
	 */
	scope: PropTypes.oneOf([
		SCOPES.SESSION,
		SCOPES.ORGANIZATION,
	]),

	/**
	 * The scope ID.
	 */
	scopeId: PropTypes.string,

	/**
	 * The provider for the video. Currently only supports Mux.
	 */
	videoProvider: PropTypes.oneOf([
		ENUMS_VIDEO.PROVIDER.MUX,
	]),

	/**
	 * The aspect ratio.
	 */
	aspectRatio: PropTypes.string,

};

const propTypes = {

	/**
	 * The field type.
	 */
	type: PropTypes.oneOf([
		FIELD_TYPES.CHECKBOX,
		FIELD_TYPES.CHECKBOXES,
		FIELD_TYPES.EMAIL,
		FIELD_TYPES.FILE_UPLOAD,
		FIELD_TYPES.DATETIME,
		FIELD_TYPES.DATETIME_RANGE,
		FIELD_TYPES.LIKERT,
		FIELD_TYPES.LOCATION,
		FIELD_TYPES.NUMBER,
		FIELD_TYPES.PASSWORD,
		FIELD_TYPES.PERCENTAGE,
		FIELD_TYPES.RADIO,
		FIELD_TYPES.SECTION,
		FIELD_TYPES.SELECT,
		FIELD_TYPES.SOCIAL_MEDIA,
		FIELD_TYPES.SWITCH,
		FIELD_TYPES.TEXT,
		FIELD_TYPES.TEXT_AREA,
		FIELD_TYPES.TEXT_EDITOR,
		FIELD_TYPES.VIDEO,
	]),

	/**
	 * The field id, to be passed to the input
	 */
	id: PropTypes.string,

	/**
	 * The field name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The field label text.
	 */
	label: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.element,
		PropTypes.array,
		PropTypes.shape({}),
	]).isRequired,

	/**
	 * The field options (if any).
	 */
	options: PropTypes.arrayOf(PropTypes.shape({

		id: PropTypes.string,

		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),

		label: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.element,
			PropTypes.array,
			PropTypes.shape({}),
		]),

	})),

	/**
	 * The field value.
	 */
	value: PropTypes.oneOfType([
		PropTypes.bool,
		PropTypes.number,
		PropTypes.string,
		PropTypes.object,
		PropTypes.instanceOf(moment),
		PropTypes.arrayOf(PropTypes.string), // checkboxes
		PropTypes.arrayOf(PropTypes.instanceOf(moment)), // datetimerange
		videoTypes.video, // VideoInput
		appPropTypes.proseMirrorDocument, // TextEditor input
	]),

	/**
	 * The input placeholder text (if any).
	 */
	placeholder: PropTypes.string,

	/**
	 * The input helper text (if any).
	 */
	help: PropTypes.oneOfType([
		PropTypes.shape({}), // JSON-serialized ProseMirror Document object.
		PropTypes.string, // Raw text
		PropTypes.element, // DOM node
		PropTypes.array,
	]),

	/**
	 * True, if the input helper text is ProseMirror WYSIWYG content.
	 */
	isHelpRichText: PropTypes.bool.isRequired,

	/**
	 * The input error text (if any).
	 */
	error: PropTypes.string,

	/**
	 * True, if editing the field value.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the field value is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * True, if the field value is required.
	 */
	isRequired: PropTypes.bool,

	/**
	 * True, if the label text should be not be visible. Will still
	 * be accessible by screen readers.
	 */
	isLabelHidden: PropTypes.bool,

	/**
	 * True, if the label text should be displayed inline with the input.
	 */
	isLabelInline: PropTypes.bool,

	/**
	 * If the form is a select that deals with languages,
	 * this flag sets the lang attributes for accessibility.
	 */
	lang: PropTypes.bool,

	/**
	 * The autocomplete attribute for the input element.
	 */
	autocomplete: PropTypes.string,

	/**
	 * If true, shows a button to copy the text for text-based
	 * form fields.
	 */
	isCopyable: PropTypes.bool,

	/**
	 * The props required for only file-upload form fields.
	 */
	...FILE_UPLOAD_FIELD_PROP_TYPES,

	/**
	 * The props required for only video input form fields.
	 */
	...VIDEO_INPUT_FIELD_PROP_TYPES,

	/**
	 * Callback function to handle changes to the input value.
	 */
	onChange: PropTypes.func,

};

const defaultProps = {
	isHelpRichText: false,
	isLabelInline: false,
	isLabelHidden: false,
};

const renderInput = (props) => {

	const {
		type,
		onChange,
		...restProps
	} = props;

	const name = restProps.name;

	switch (type) {
	case FIELD_TYPES.TEXT:
	case FIELD_TYPES.SOCIAL_MEDIA:
		return (
			<Input
				{...restProps}
				type="text"
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.TEXT_AREA:
		return (
			<Input
				{...restProps}
				type="textarea"
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.TEXT_EDITOR:
		return (
			<TextEditorInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.EMAIL:
		return (
			<Input
				{...restProps}
				type="email"
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.PASSWORD:
		return (
			<PasswordInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.NUMBER:
		return (
			<Input
				{...restProps}
				type="number"
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.SELECT:
		return (
			<SelectInput
				{...restProps}
				onChange={(newValue, optionID) => onChange(name, newValue, optionID)} />
		);
	case FIELD_TYPES.RADIO:
		return (
			<RadioInput
				{...restProps}
				onChange={(newValue, optionID) => onChange(name, newValue, optionID)} />
		);
	case FIELD_TYPES.CHECKBOX:
		return (
			<CheckboxInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.CHECKBOXES:
		return (
			<CheckboxesInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.LOCATION:
		return (
			<LocationInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)}
				onChangeMeta={(newValue) => onChange(`${name}:meta`, newValue)} />
		);
	case FIELD_TYPES.FILE_UPLOAD:
		return (
			<FileInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.LIKERT:
		return (
			<LikertScaleInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.PERCENTAGE:
		return (
			<PercentageInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.DATETIME:
		return (
			<DateTimeInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.DATETIME_RANGE:
		return (
			<DateTimeRangeInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.SWITCH:
		return (
			<SwitchInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)} />
		);
	case FIELD_TYPES.VIDEO:
		return (
			<VideoInput
				{...restProps}
				onChange={(newValue) => onChange(name, newValue)} />
		);


	default:
		console.warn(`This input type is not supported: ${type}.`);
		return (
			<Input
				{...restProps}
				type="text"
				onChange={(newValue) => onChange(name, newValue)} />
		);
	}

};

/**
 * Shared component to render a form field.
 *
 * @param {Object} props The props.
 * @returns {Object} The rendered component.
 */
const FormField = (props) => {

	const {
		type,
		id,
		name,
		label,
		help,
		isHelpRichText,
		error,
		isEditing,
		isInvalid,
		isRequired,
		isLabelInline,
		isLabelHidden,

		/* eslint-disable no-unused-vars */

		// Remove unnecessary props passed to FormGroup
		value,
		placeholder,
		options,
		autocomplete,
		lang,
		isAdmin,
		isInline,
		isPreview,
		disabled,
		config,
		defaultTimezone,
		accept,
		visibility,
		filestack,
		videoProvider,
		scope,
		scopeId,
		filestackSettings,
		emojiSize,
		innerClassName,
		onChange,
		editorDomId,
		mobileview,
		aspectRatio,
		isCopyable,

		/* eslint-enable no-unused-vars */

		...restProps
	} = props;

	const intl = useIntl();

	const [feedbackID] = useState(`feedback-msg-${Math.random()}`);

	let errorMsg = error;

	// Fill in a default error message for required fields (as needed)
	if (isInvalid && isRequired && !error) {

		errorMsg = intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_REQUIRED);

	}

	// Check if we need to show any feedback text, i.e. error or help text
	let helpText = null;

	let errorText = null;

	if (isEditing) {

		// Check if there is any error text to show
		if (isInvalid && errorMsg) {
			errorText = (
				<FormFeedback
					id={feedbackID}
					aria-live="assertive">
					{errorMsg}
				</FormFeedback>
			);
		}

		// Check if there is any helper text to show
		if (help) {

			if (isHelpRichText) {

				if (!isEmpty(help)) {

					helpText = (
						<TextEditorInline
							className="tc-form-field-help-text"
							editorDomId={feedbackID}
							aria-live="assertive"
							content={help}
							readOnly />
					);

				}

			} else {

				helpText = (
					<FormText
						id={feedbackID}
						className="tc-form-field-help-text"
						aria-live="assertive"
						tag="div">
						{help}
					</FormText>
				);

			}

		}

	}

	// Render the help text below the checkbox or switch if the label is inline
	const isLabelInlineCheckboxOrSwitch =
		(type === FIELD_TYPES.CHECKBOX || type === FIELD_TYPES.SWITCH) && isLabelInline;

	return (
		<FormGroup
			{...restProps}
			className={`tc-form-field ${restProps.className || ''}`}
			tag={type === FIELD_TYPES.RADIO ? 'fieldset' : 'div'}>
			{!isLabelInline &&
				<>
					{type === FIELD_TYPES.RADIO
						? (
							<legend
								htmlFor={id || name}
								className={`tc-form-field-label-text ${isLabelHidden ? 'visually-hidden' : ''}`}>
								{label}
								{isEditing && isRequired &&
									<span>{' *'}</span>
								}
							</legend>
						) : (
							<Label
								for={id || name}
								className={`tc-form-field-label-text ${isLabelHidden ? 'visually-hidden' : ''}`}>
								{label}
								{isEditing && isRequired &&
									<span>{' *'}</span>
								}
							</Label>
						)
					}
				</>
			}
			{!isLabelInlineCheckboxOrSwitch && helpText}
			{renderInput({ ...props, feedbackID })}
			{isLabelInlineCheckboxOrSwitch && helpText}
			{errorText}
		</FormGroup>
	);

};

renderInput.propTypes = propTypes;

FormField.propTypes = propTypes;
FormField.defaultProps = defaultProps;

export default FormField;
